import { MAX_SOURCE_URL_LENGTH, urlMatchesHostSuffixes } from '@kiao-voice/core'
import { describeValidationError } from './file-validation'
import type { AudioBatchValidationError } from './types'

/** The 「貼上連結」 textarea, read one link per line. */
export interface ParsedSourceLinks {
  /** Every line that is a usable http(s) link, trimmed, in the order typed. */
  urls: string[]
  /** Lines that are not a usable link; Start is refused while any remain. */
  invalid: string[]
  /**
   * Usable links outside the media-platform list. Still sent: the worker tries
   * them as a direct file download. Listed so the dialog can say so up front.
   */
  offPlatform: string[]
}

/**
 * Split the textarea into links. The same rule the API applies (http(s), at
 * most {@link MAX_SOURCE_URL_LENGTH} characters), checked here so a typo is
 * caught before anything is sent; the server re-validates regardless.
 */
export function parseSourceLinks(text: string): ParsedSourceLinks {
  const urls: string[] = []
  const invalid: string[] = []
  const offPlatform: string[] = []
  for (const raw of text.split('\n')) {
    const line = raw.trim()
    if (line === '') continue
    if (!isUsableSourceUrl(line)) {
      invalid.push(line)
      continue
    }
    urls.push(line)
    if (!urlMatchesHostSuffixes(line)) offPlatform.push(line)
  }
  return { urls, invalid, offPlatform }
}

function isUsableSourceUrl(value: string): boolean {
  if (value.length > MAX_SOURCE_URL_LENGTH || !URL.canParse(value)) return false
  const url = new URL(value)
  return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname !== ''
}

/**
 * A 422 on a link submission, in words. Two refusals read differently from the
 * file flow: a bad link is named by its URL rather than its position, and
 * `no_files` means the server ignored `sourceUrls` entirely, which only an API
 * older than link mode does (the dialog never sends an empty list).
 */
export function describeLinkValidationError(
  err: AudioBatchValidationError,
  urls: readonly string[],
  t: (key: string, values?: Record<string, string | number>) => string
): string {
  if (err.kind === 'invalid_source_url' && urls[err.index] !== undefined) {
    return t('transcriptions.upload.linksInvalid', { url: urls[err.index] })
  }
  if (err.kind === 'no_files') return t('transcriptions.upload.linksUnsupported')
  return describeValidationError(err, t)
}
